import { QMenuButton, QMenuItem } from '@qualio/ui-components';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { TemplateDetail, TemplateVersions } from '../../types';
import { useNavigate } from 'react-router-dom';

export type DisplayVersionSelectProps = {
  currentVersionId: number;
  versions: TemplateVersions;
};

const buildVersion = (template: TemplateDetail) => `${template.major_version}.${template.minor_version}`;

export const TemplateVersionSelect: React.FC<DisplayVersionSelectProps> = ({ currentVersionId, versions }) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const isDisabled = useMemo(() => versions.versions.length === 1, [versions]);

  const selectedLabel = useMemo(() => {
    const v = versions.versions.find((v: TemplateDetail) => v.id === currentVersionId);
    if (v) {
      return v.id === versions.latest_version_id
        ? `${t('template.version.label')} ${buildVersion(v)} ${t('template.version.latest')}`
        : `${t('template.version.label')} ${buildVersion(v)}`;
    }
  }, [versions, currentVersionId, t]);

  const options = useMemo(() => {
    return versions.versions
      .filter((v) => v.id !== currentVersionId)
      .map((v) => (
        <QMenuItem
          key={`${v.id}-version-key`}
          fontSize="sm"
          onClick={() => navigate(`/doc-templates/${v.id}`)}
          data-cy={`select-template-version-${v.id}`}
        >
          {v.id === versions.latest_version_id
            ? `${t('template.version.label')} ${buildVersion(v)} ${t('template.version.latest')}`
            : `${t('template.version.label')} ${buildVersion(v)}`}
        </QMenuItem>
      ));
  }, [versions, currentVersionId, navigate, t]);

  return (
    <QMenuButton
      data-cy="template-versions-dropdown"
      variant="ghost"
      buttonLabel={selectedLabel}
      isDisabled={isDisabled}
    >
      {options}
    </QMenuButton>
  );
};
